/**
 * Sign in (email and password; an account whose email is not verified yet,
 * or a legacy one with none, may use its username), sign up (alpha invite code, email, display name, username
 * with live validation, password, birth date (Gregorian),
 * 18+), the two-step prompt, forgot password and the reset link, and the
 * recovery-code screen after sign-up. Errors sit next to their field in
 * calm wording; nothing typed is lost.
 */
import {
  ApiErrorCode,
  FieldErrorCode,
  isMfaRequired,
  parseTwoStepCode,
  validateBirthDate,
  validateDisplayName,
  validateEmail,
  validatePassword,
  validateUsername,
  visibleConsoleText,
  type AuthSessionResponse,
  type MfaRequiredResponse,
  type PowChallengeRequest,
  type PowSolution,
} from '@neosocial/protocol';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { tr, useT } from '../i18n';
import { api, ApiRequestError, isApiRequestError, NetworkError } from '../lib/api';
import { clientInfo } from './clientInfo';
import { solvePow } from '../lib/pow';
import { storage } from '../lib/storage';
import { useUi } from '../store/ui';
import { startSession, useSession } from '../store/session';
import { loadConfig, useConfig } from '../store/config';
import { useAppName, useWordmark } from '../store/consoleDefaults';
import { localized, raidNow } from '../shell/ConfigNotices';
import { setTitleName } from '../notify/title';
import { Btn, Icon } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import markUrl from '../assets/mark.svg';
import { BirthDate, toIso, type BirthValue } from './BirthDate';
import { Recovery, savedRecovery } from './Recovery';
import { DesktopServerLink } from '../settings/Server';
import { isLeakedPassword } from './leaked';
import { PasswordInput } from './PasswordInput';
import { autoFocusOk, useSubmitInView } from './keyboard';
import { forgetLinkParam, takeLinkParam } from './links';
import { selfHoldText } from '../social/removal';
import { appealTokenOf, SuspendedAppeal } from '../appeals/AppealDialog';
import { InviteContext, inviteLinkInAddress, takeInviteLink } from '../shell/InviteLink';

export type AuthStep = 'signin' | 'signup' | 'mfa' | 'underage' | 'recover' | 'forgot' | 'reset';
type Step = AuthStep;

const UNDERAGE_KEY = 'signup-blocked-until';

/** Raid mode is on: sign-up is paused for a while, not closed (Console v2 decision 9). */
function useSignupPaused(): boolean {
  return useConfig((s) => !!raidNow(s.config));
}

/** The operator's sign-up message for `mode` (console › Sign-up), else our own words. */
function useSignupText(): (mode: 'open' | 'invite_only' | 'closed') => string {
  const { t } = useT();
  const settings = useConfig((s) => s.config?.settings);
  const paused = useSignupPaused();
  return (mode) => {
    // raid mode closes sign-up with its own calm text (`texts.raid_signups`),
    // as the server's refusal does; "closed" would read as closed for good
    if (mode === 'closed' && paused)
      return localized(settings?.['texts.raid_signups']).trim() || t('gov.signup.paused');
    const own = localized(settings?.[`texts.signup_${mode}`]).trim();
    if (own) return own;
    return mode === 'closed'
      ? t('ns.err.100036')
      : mode === 'invite_only'
        ? t('auth.signup.inviteOnly')
        : '';
  };
}

// in its own module since account recovery (Recovery.tsx) sends it too
export { clientInfo };

function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="ns-field">
      <span className="ns-label">{label}</span>
      {children}
      {error ? (
        <span className="ns-err" role="alert">
          {error}
        </span>
      ) : help ? (
        <span className="ns-help">{help}</span>
      ) : null}
    </label>
  );
}

/** A console text (`texts.*`); empty when the console left it empty. */
function useConsoleText(
  key: 'texts.welcome' | 'texts.rules' | 'texts.terms' | 'texts.privacy' | 'texts.data_notice',
): string {
  // the staff-review clause's marker (Console v2) is an HTML comment: never shown
  return visibleConsoleText(localized(useConfig((s) => s.config?.settings[key])));
}

/** The console's welcome line (`texts.welcome`), under the sign-in and sign-up titles. */
function Welcome() {
  const text = useConsoleText('texts.welcome');
  const known = useConfig((s) => s.status !== 'unknown');
  // Until the config answers, its line is kept (empty), so the form doesn't
  // jump under a pointer about to click "Create your account".
  if (!text && known) return null;
  return (
    <p
      className="ns-auth-sub"
      dir="auto"
      data-console-text="welcome"
      aria-hidden={!text || undefined}
    >
      {text || ' '}
    </p>
  );
}

/**
 * The console's rules, terms and privacy texts as links that open them, and
 * its data notice, at the foot of the sign-up form (QA CO-BUG-15). A text
 * the console left empty shows nothing.
 */
function SignupTexts() {
  const { t } = useT();
  const rules = useConsoleText('texts.rules');
  const terms = useConsoleText('texts.terms');
  const privacy = useConsoleText('texts.privacy');
  const notice = useConsoleText('texts.data_notice');
  const [open, setOpen] = useState<{ title: string; text: string } | null>(null);
  const links = (
    [
      ['cfg.texts.rules', rules],
      ['cfg.texts.terms', terms],
      ['cfg.texts.privacy', privacy],
    ] as const
  ).filter(([, text]) => text);
  if (!links.length && !notice) return null;
  return (
    <div className="ns-auth-texts" data-console-text="signup">
      {links.length ? (
        <p className="ns-auth-alt ns-auth-links">
          {links.map(([k, text]) => (
            <button
              key={k}
              type="button"
              className="ns-link"
              onClick={() => setOpen({ title: t(k), text })}
            >
              {t(k)}
            </button>
          ))}
        </p>
      ) : null}
      {notice ? (
        <p className="ns-help" dir="auto">
          {notice}
        </p>
      ) : null}
      {open ? (
        <Dialog title={open.title} onClose={() => setOpen(null)} width={560}>
          <div className="ns-auth-text-body" dir="auto">
            {open.text}
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

/** Network and server errors, said plainly. */
export function errorText(
  e: unknown,
  t: (k: string, v?: Record<string, string | number>) => string,
): string {
  if (e instanceof NetworkError) return t('ns.err.network');
  if (e instanceof ApiRequestError) {
    if (e.status === 429) return t('auth.signin.wait', { n: Math.ceil(e.retryAfter ?? 30) });
    // the account's own Disable or Delete account: how to come back (2026-10-29)
    const own = selfHoldText(e.body);
    if (own) return own;
    const key = `ns.err.${e.code}`;
    // Suspended, disabled (deletion hold) or limited by staff: say until when.
    const until = e.body?.nx_until;
    if (until && tr().has(`${key}.until`)) return t(`${key}.until`, { date: tr().fmt.date(until) });
    const s = t(key);
    if (s !== key) return s;
  }
  return t('ns.err.generic');
}

/**
 * Runs a call that may ask for proof of work first. Sign-in asks for the
 * `login` puzzle: the server wants it once an account has had many failed
 * sign-ins, and it is harder than the sign-up one.
 */
async function withPow<T>(
  fn: (pow?: PowSolution) => Promise<T>,
  onWorking: (on: boolean) => void,
  purpose: PowChallengeRequest['purpose'] = 'register',
): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (
      !isApiRequestError(e, ApiErrorCode.POW_REQUIRED) &&
      !isApiRequestError(e, ApiErrorCode.POW_INVALID)
    )
      throw e;
  }
  onWorking(true);
  try {
    const c = await api.auth.powChallenge({ purpose });
    const sol = await solvePow(c);
    return await fn(sol);
  } finally {
    onWorking(false);
  }
}

function SignIn({ go, onMfa }: { go(s: Step): void; onMfa(m: MfaRequiredResponse): void }) {
  const { t } = useT();
  const mode = useConfig((s) => s.config?.signup_mode ?? null);
  const signupText = useSignupText();
  const ended = useSession((s) => s.endedReason);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [pow, setPow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // both fields are marked until the next edit (I27); the banner stays until the next try
  const [invalid, setInvalid] = useState(false);
  const [appeal, setAppeal] = useState<string | null>(null);
  const pwRef = useRef<HTMLInputElement>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!login.trim() || !password) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await withPow(
        (p) =>
          api.auth.login({
            login: login.trim(),
            password,
            client: clientInfo(),
            ...(p ? { pow: p } : {}),
          }),
        setPow,
        'login',
      );
      if (isMfaRequired(r)) onMfa(r);
      else startSession(r);
    } catch (er) {
      const wrong =
        isApiRequestError(er, ApiErrorCode.INVALID_CREDENTIALS) ||
        (er instanceof ApiRequestError && er.status === 401);
      setErr(wrong ? t('auth.signin.wrong') : errorText(er, t));
      setAppeal(appealTokenOf(er));
      if (wrong) {
        setInvalid(true);
        // the password is what to type again
        requestAnimationFrame(() => {
          pwRef.current?.focus();
          pwRef.current?.select();
        });
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ns-auth-form" onSubmit={submit} noValidate>
      <h1>{t('auth.signin.title')}</h1>
      <Welcome />
      {ended ? (
        // the server ended the last session: say why (suspended until, being deleted...)
        <div className="ns-auth-notice" role="alert">
          <b>{t('auth.ended.title')}</b>
          <span>{errorText(new ApiRequestError(403, ended), t)}</span>
        </div>
      ) : null}
      {/* An email; an account with no verified email (legacy, or not yet verified) may type its username. */}
      <Field label={t('auth.email.label')} help={t('auth.signin.loginHelp')}>
        <input
          className="nx-input"
          dir="ltr"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={login}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? 'signin-err' : undefined}
          onChange={(e) => (setLogin(e.target.value), setInvalid(false))}
          data-autofocus
          autoFocus={autoFocusOk()}
        />
      </Field>
      <Field label={t('auth.password.label')}>
        <PasswordInput
          ref={pwRef}
          autoComplete="current-password"
          value={password}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? 'signin-err' : undefined}
          onChange={(e) => (setPassword(e.target.value), setInvalid(false))}
        />
      </Field>
      {err ? (
        <p className="ns-err ns-form-err ns-auth-err" role="alert" id="signin-err">
          {err}
        </p>
      ) : null}
      {appeal ? <SuspendedAppeal token={appeal} /> : null}
      {pow ? (
        <p className="ns-help" role="status">
          <i className="nx-spin" /> {t('auth.pow.working')}
        </p>
      ) : null}
      <Btn
        kind="primary"
        type="submit"
        disabled={!login.trim() || !password || busy}
        busy={busy}
        className="ns-wide ns-auth-submit"
      >
        {t('ns.signin.submit')}
      </Btn>
      {mode === 'closed' ? (
        // sign-up is closed in the console: its message, and no way into a form that can't work
        <p className="ns-auth-alt ns-signup-closed" dir="auto">
          {signupText('closed')}
        </p>
      ) : (
        <p className="ns-auth-alt">
          {t('ns.signin.new')}{' '}
          <button type="button" className="ns-link" onClick={() => go('signup')}>
            {t('auth.signup.title')}
          </button>
        </p>
      )}
      <p className="ns-auth-alt ns-auth-links">
        <button type="button" className="ns-link" onClick={() => go('forgot')}>
          {t('auth.forgot.link')}
        </button>
        <button type="button" className="ns-link" onClick={() => go('recover')}>
          {t('recovery.code')}
        </button>
      </p>
    </form>
  );
}

function Mfa({ ticket, go }: { ticket: MfaRequiredResponse; go(s: Step): void }) {
  const { t } = useT();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [appeal, setAppeal] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    // The code field also takes a recovery code, told apart by form (8 digits,
    // not 6), as the step-up and the console do: Discord's "Auth/Backup code".
    const typed = parseTwoStepCode(code);
    const backup =
      !recovery && typed?.kind === 'recovery' && ticket.methods.includes('recovery')
        ? typed.code
        : null;
    const asRecovery = recovery || backup !== null;
    try {
      const body = {
        ticket: ticket.ticket,
        code: recovery ? code.trim() : (backup ?? code.replace(/\s/g, '')),
        // this device, so the session shows as it (and the web or desktop switch applies)
        client: clientInfo(),
      };
      const r: AuthSessionResponse = asRecovery
        ? await api.auth.mfaRecovery(body)
        : await api.auth.mfaTotp(body);
      startSession(r);
    } catch (er) {
      if (isApiRequestError(er, ApiErrorCode.INVALID_MFA_CODE))
        setErr(t(asRecovery ? 'auth.mfa.recoveryWrong' : 'auth.mfa.wrong'));
      else if (isApiRequestError(er, ApiErrorCode.INVALID_MFA_TICKET)) {
        setErr(t('ns.mfa.expired'));
      } else setErr(errorText(er, t));
      setAppeal(appealTokenOf(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ns-auth-form" onSubmit={submit} noValidate>
      <h1>{t('auth.mfa.title')}</h1>
      <p className="ns-auth-sub">{recovery ? t('ns.mfa.recoveryHelp') : t('auth.mfa.totp')}</p>
      <Field label={recovery ? t('recovery.code') : t('ns.mfa.code')} error={err}>
        <input
          className="nx-input ns-code-in"
          dir="ltr"
          inputMode={recovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
          aria-invalid={!!err || undefined}
        />
      </Field>
      <Btn
        kind="primary"
        type="submit"
        disabled={busy || (recovery ? code.trim().length < 6 : code.replace(/\s/g, '').length < 6)}
        busy={busy}
        className="ns-wide ns-auth-submit"
      >
        {t('ns.mfa.verify')}
      </Btn>
      {appeal ? <SuspendedAppeal token={appeal} /> : null}
      {ticket.methods.includes('recovery') ? (
        <p className="ns-auth-alt">
          <button
            type="button"
            className="ns-link"
            onClick={() => {
              setRecovery(!recovery);
              setCode('');
              setErr(null);
            }}
          >
            {recovery ? t('ns.mfa.useApp') : t('auth.mfa.useCode')}
          </button>
        </p>
      ) : null}
      <p className="ns-auth-alt">
        <button type="button" className="ns-link" onClick={() => go('signin')}>
          {t('c.back')}
        </button>
      </p>
    </form>
  );
}

function usernameMessage(name: string, t: (k: string) => string): string | null {
  if (!name) return null;
  const r = validateUsername(name);
  if (r.ok) return null;
  switch (r.error) {
    case 'non_latin':
      return t('auth.username.latinHint');
    case 'consecutive_dots':
      return t('auth.username.doubleDot');
    case 'reserved':
      return t('auth.username.reserved');
    case 'too_short':
    case 'too_long':
    case 'invalid_chars':
      return t('auth.username.help');
  }
}

/**
 * A console invite link (`PUBLIC_URL/?signup_code=CODE`): the code, read
 * once per page load, and the parameter is taken off the address bar. It
 * serves the first sign-in screen only (`forgetSignupLink` when that screen
 * goes away), so a later sign-out lands on sign-in, not sign-up.
 */
let linkCode: string | null | undefined;
// the code came in this page's address (a sign-up or invite link just opened)
let linkOpened = false;
export function signupCodeFromLink(): string | null {
  if (linkCode !== undefined) return linkCode;
  linkCode = null;
  linkOpened = false;
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get('signup_code')?.trim();
    if (v && /^[A-Za-z0-9_-]{1,64}$/.test(v)) {
      linkCode = v;
      linkOpened = true;
    }
    if (url.searchParams.has('signup_code')) {
      url.searchParams.delete('signup_code');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* no window (tests) or a strange URL */
  }
  // a server invite link (/invite/CODE) works as a sign-up code too; it is
  // not used up here: after sign-up the invited channel opens (InviteLinkHost)
  if (!linkCode) {
    const invite = takeInviteLink();
    if (invite && /^[A-Za-z0-9-]{2,32}$/.test(invite)) linkCode = invite;
  }
  return linkCode;
}
/**
 * Sign-up comes first only for a link opened just now (a sign-up link, or an
 * invite link in the address): an invite code merely kept in the tab from
 * earlier fills the code but leaves sign-in first (QA integration, AA-B18).
 */
function signupLinkOpened(): boolean {
  signupCodeFromLink();
  return linkOpened || inviteLinkInAddress();
}
export function forgetSignupLink(): void {
  linkCode = null;
  linkOpened = false;
}
/** Tests only. */
export function resetSignupLink(): void {
  linkCode = undefined;
  linkOpened = false;
}

function SignUp({ go }: { go(s: Step): void }) {
  const { t } = useT();
  // The console's sign-up mode (AA-B05, CO-BUG-07): open needs no code (and
  // shows no code field), closed shows the console's message instead of the
  // form. Before the server's first answer, as invite-only (the server's
  // default: a code field that may not be needed).
  const mode = useConfig((s) => s.config?.signup_mode) ?? 'invite_only';
  const signupText = useSignupText();
  const paused = useSignupPaused();
  const needCode = mode !== 'open';
  // a visitor has no gateway: ask again now and then, so a change shows here live
  useEffect(() => {
    const id = setInterval(() => void loadConfig({ force: true }), 20_000);
    return () => clearInterval(id);
  }, []);
  const [code, setCode] = useState(() => signupCodeFromLink() ?? '');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [birth, setBirth] = useState<BirthValue>({ year: null, month: null, day: null });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [server, setServer] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [pow, setPow] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const emailCheck = validateEmail(email);
  const emailMsg =
    server.email ||
    ((touched.email || touched.password) && email && !emailCheck.ok
      ? t('auth.email.invalid')
      : null);
  const userMsg = server.username || usernameMessage(username, t);
  const dnCheck = displayName.trim() ? validateDisplayName(displayName) : null;
  const dnMsg = server.global_name || (dnCheck && !dnCheck.ok ? t(`ns.dn.${dnCheck.error}`) : null);
  const pwCheck = validatePassword(password);
  const pwMsg =
    server.password ||
    (touched.password && password && !pwCheck.ok ? t('auth.password.help') : null);
  const iso = toIso(birth);
  const birthCheck = iso ? validateBirthDate(iso) : null;
  const birthMsg =
    server.birth_date ||
    (birth.year && birth.month && birth.day && !iso
      ? t('ns.birth.invalid')
      : birthCheck && !birthCheck.ok && birthCheck.error !== 'underage'
        ? t('ns.birth.invalid')
        : null);
  const ready =
    (!needCode || code.trim()) &&
    emailCheck.ok &&
    username &&
    !usernameMessage(username, t) &&
    pwCheck.ok &&
    iso &&
    !(dnCheck && !dnCheck.ok);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ password: true, email: true });
    if (!ready || !iso) return;
    // under 18: stop calmly, keep nothing, and pause sign-up on this device for a day
    if (birthCheck && !birthCheck.ok && birthCheck.error === 'underage') {
      storage.set(UNDERAGE_KEY, String(Date.now() + 24 * 3600_000));
      go('underage');
      return;
    }
    setBusy(true);
    setServer({});
    setFormErr(null);
    try {
      const r = await withPow(
        (p) =>
          api.auth.register({
            email: emailCheck.value,
            username: username.trim(),
            global_name: displayName.trim() || null,
            password,
            birth_date: iso,
            // open sign-up sends no code: an invite link's code (kept for after
            // sign-up) must not fail a form whose code field isn't shown
            ...(needCode ? { alpha_code: code.trim() } : {}),
            client: clientInfo(),
            locale: 'en',
            // the theme shown now (the console's default where this device chose none),
            // so it carries on after sign-in
            theme: useUi.getState().theme,
            ...(p ? { pow: p } : {}),
          }),
        setPow,
      );
      startSession(r);
    } catch (er) {
      if (er instanceof ApiRequestError) {
        if (er.code === ApiErrorCode.UNDERAGE) {
          storage.set(UNDERAGE_KEY, String(Date.now() + 24 * 3600_000));
          go('underage');
          return;
        }
        if (er.code === ApiErrorCode.INVALID_ALPHA_CODE) {
          setServer({ alpha_code: t('auth.alpha.invalid') });
          return;
        }
        if (er.code === ApiErrorCode.SIGNUP_CLOSED) {
          // the server says why (its closed text, or raid mode's while it runs),
          // and the config is asked again so the screen follows
          setFormErr(er.body?.message?.trim() || signupText('closed'));
          void loadConfig({ force: true });
          return;
        }
        const f = er.fieldCodes();
        const next: Record<string, string> = {};
        if (f.email === FieldErrorCode.EMAIL_TAKEN) next.email = t('auth.email.taken');
        else if (f.email) next.email = t('auth.email.invalid');
        if (f.username === FieldErrorCode.USERNAME_TAKEN) next.username = t('ns.username.taken');
        else if (f.username) next.username = t('auth.username.help');
        if (isLeakedPassword(er, 'password')) next.password = t('auth.password.breached');
        else if (f.password) next.password = t('auth.password.help');
        if (f.global_name) next.global_name = t('auth.displayName.protected');
        if (f.birth_date) next.birth_date = t('ns.birth.invalid');
        if (f.alpha_code) next.alpha_code = t('auth.alpha.invalid');
        if (Object.keys(next).length) setServer(next);
        else setFormErr(errorText(er, t));
      } else setFormErr(errorText(er, t));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'closed') {
    return (
      <div className="ns-auth-form" role="status" data-signup-mode="closed">
        <span className="ns-auth-ic">
          <Icon name="lock" />
        </span>
        <h1>{t(paused ? 'gov.signup.pausedTitle' : 'auth.signup.closedTitle')}</h1>
        <p className="ns-auth-sub" dir="auto">
          {signupText('closed')}
        </p>
        <Btn kind="primary" onClick={() => go('signin')} className="ns-wide">
          {t('ns.signin.submit')}
        </Btn>
      </div>
    );
  }
  const modeText = signupText(mode);
  return (
    // a field's hint shows while it has the focus (Discord's sign-up), so the
    // card fits a 1280x800 window; an error always shows (§7.29)
    <form
      className="ns-auth-form ns-auth-signup"
      onSubmit={submit}
      noValidate
      data-signup-mode={mode}
    >
      <h1>{t('auth.signup.title')}</h1>
      <Welcome />
      {modeText ? (
        <p className="ns-auth-sub" dir="auto">
          {modeText}
        </p>
      ) : null}
      {needCode ? (
        <Field label={t('auth.alpha.code')} error={server.alpha_code}>
          <input
            className="nx-input"
            name="alpha_code"
            dir="ltr"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            aria-invalid={!!server.alpha_code || undefined}
            onChange={(e) => (
              setCode(e.target.value),
              setServer((s) => ({ ...s, alpha_code: '' }))
            )}
            autoFocus={autoFocusOk()}
          />
        </Field>
      ) : null}
      <Field label={t('auth.email.label')} help={t('auth.email.help')} error={emailMsg}>
        <input
          className="nx-input"
          type="email"
          name="email"
          dir="ltr"
          inputMode="email"
          // the sign-in identifier, so a password manager saves the email (not the @handle)
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          aria-invalid={!!emailMsg || undefined}
          onBlur={() => setTouched((x) => ({ ...x, email: true }))}
          onChange={(e) => (setEmail(e.target.value), setServer((s) => ({ ...s, email: '' })))}
        />
      </Field>
      {/* the two names side by side, so the card fits a 1280x800 window (§7.29) */}
      <div className="ns-auth-2col">
        <Field label={t('auth.displayName.label')} help={t('auth.displayName.help')} error={dnMsg}>
          <input
            className="nx-input"
            dir="auto"
            autoComplete="nickname"
            value={displayName}
            aria-invalid={!!dnMsg || undefined}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <Field
          label={t('auth.username.label')}
          help={`${t('auth.username.handle')} ${t('auth.username.help')}`}
          error={userMsg || null}
        >
          <input
            className="nx-input"
            name="handle"
            dir="ltr"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            aria-invalid={!!userMsg || undefined}
            onChange={(e) => {
              setUsername(e.target.value.toLowerCase());
              setServer((s) => ({ ...s, username: '' }));
            }}
          />
        </Field>
      </div>
      <Field label={t('auth.password.label')} help={t('auth.password.help')} error={pwMsg}>
        <PasswordInput
          autoComplete="new-password"
          value={password}
          aria-invalid={!!pwMsg || undefined}
          onBlur={() => setTouched((x) => ({ ...x, password: true }))}
          onChange={(e) => (
            setPassword(e.target.value),
            setServer((s) => ({ ...s, password: '' }))
          )}
        />
      </Field>
      <div className="ns-field">
        <span className="ns-label">{t('auth.birth.label')}</span>
        <BirthDate value={birth} onChange={setBirth} invalid={!!birthMsg} />
        {birthMsg ? (
          <span className="ns-err" role="alert">
            {birthMsg}
          </span>
        ) : (
          <span className="ns-help">{t('auth.birth.help', { brand: t('ns.brand') })}</span>
        )}
      </div>
      {formErr ? (
        <p className="ns-err ns-form-err ns-auth-err" role="alert">
          {formErr}
        </p>
      ) : null}
      {pow ? (
        <p className="ns-help" role="status">
          <i className="nx-spin" /> {t('auth.pow.working')}
        </p>
      ) : null}
      <Btn
        kind="primary"
        type="submit"
        disabled={!ready || busy}
        busy={busy}
        className="ns-wide ns-auth-submit"
      >
        {t('auth.signup.submit')}
      </Btn>
      <SignupTexts />
      <p className="ns-auth-alt">
        {t('ns.signup.have')}{' '}
        <button type="button" className="ns-link" onClick={() => go('signin')}>
          {t('ns.signin.submit')}
        </button>
      </p>
    </form>
  );
}

/** Forgot password: the same answer whether or not an account has the address (the server's rule). */
function Forgot({ go }: { go(s: Step): void }) {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pow, setPow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const check = validateEmail(email);
  const msg = touched && email && !check.ok ? t('auth.email.invalid') : null;
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (!check.ok) return;
    setBusy(true);
    setErr(null);
    try {
      await withPow(
        (p) => api.auth.forgotPassword({ email: check.value, ...(p ? { pow: p } : {}) }),
        setPow,
      );
      setSentTo(check.value);
    } catch (er) {
      setErr(
        er instanceof ApiRequestError && er.fieldCodes().email
          ? t('auth.email.invalid')
          : er instanceof ApiRequestError && er.status === 429
            ? // links asked for earlier keep working: say so, and how long to wait otherwise
              t('auth.forgot.wait', { n: Math.max(1, Math.ceil((er.retryAfter ?? 60) / 60)) })
            : errorText(er, t),
      );
    } finally {
      setBusy(false);
    }
  };
  if (sentTo) {
    return (
      <div className="ns-auth-form" role="status">
        <span className="ns-auth-ic">
          <Icon name="mail" />
        </span>
        <h1>{t('auth.forgot.sentTitle')}</h1>
        <p className="ns-auth-sub">{t('auth.forgot.sent', { email: sentTo })}</p>
        {err ? (
          <p className="ns-err ns-form-err ns-auth-err" role="alert">
            {err}
          </p>
        ) : null}
        <Btn kind="primary" onClick={() => go('signin')} className="ns-wide">
          {t('ns.signin.submit')}
        </Btn>
        <p className="ns-auth-alt ns-auth-links">
          <button type="button" className="ns-link" disabled={busy} onClick={() => void send()}>
            {t('auth.forgot.again')}
          </button>
          <button type="button" className="ns-link" onClick={() => go('recover')}>
            {t('recovery.code')}
          </button>
        </p>
      </div>
    );
  }
  return (
    <form className="ns-auth-form" onSubmit={send} noValidate>
      <h1>{t('auth.forgot.title')}</h1>
      <p className="ns-auth-sub">{t('auth.forgot.body')}</p>
      <Field label={t('auth.email.label')} error={msg}>
        <input
          className="nx-input"
          type="email"
          name="email"
          dir="ltr"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          aria-invalid={!!msg || undefined}
          onBlur={() => setTouched(true)}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus={autoFocusOk()}
        />
      </Field>
      {err ? (
        <p className="ns-err ns-form-err ns-auth-err" role="alert">
          {err}
        </p>
      ) : null}
      {pow ? (
        <p className="ns-help" role="status">
          <i className="nx-spin" /> {t('auth.pow.working')}
        </p>
      ) : null}
      <Btn
        kind="primary"
        type="submit"
        disabled={!check.ok || busy}
        busy={busy}
        className="ns-wide ns-auth-submit"
      >
        {t('auth.forgot.send')}
      </Btn>
      <p className="ns-help">{t('auth.forgot.noEmail')}</p>
      <p className="ns-auth-alt ns-auth-links">
        <button type="button" className="ns-link" onClick={() => go('recover')}>
          {t('recovery.code')}
        </button>
        <button type="button" className="ns-link" onClick={() => go('signin')}>
          {t('c.back')}
        </button>
      </p>
    </form>
  );
}

/** The link from the reset email (`?reset_password=`): a new password, then sign in again. */
export function ResetPassword({
  token,
  go,
  onBack,
}: {
  token: string;
  go(s: Step): void;
  /** "Back" (default: to sign-in). Signed in, it only closes the dialog. */
  onBack?: () => void;
}) {
  const back = onBack ?? (() => go('signin'));
  const { t } = useT();
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [state, setState] = useState<'form' | 'done' | 'invalid'>('form');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ok = validatePassword(password).ok;
  const msg = err || (touched && password && !ok ? t('auth.password.help') : null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await api.auth.resetPassword({ token, new_password: password });
      forgetLinkParam('reset_password');
      setState('done');
    } catch (er) {
      const f = er instanceof ApiRequestError ? er.fieldCodes() : {};
      if (isApiRequestError(er, ApiErrorCode.RESET_LINK_INVALID)) {
        forgetLinkParam('reset_password');
        setState('invalid');
      } else if (f.new_password === 'PASSWORD_LEAKED') setErr(t('auth.password.breached'));
      else if (f.new_password) setErr(t('auth.password.help'));
      else setErr(errorText(er, t));
    } finally {
      setBusy(false);
    }
  };
  if (state !== 'form') {
    const done = state === 'done';
    return (
      <div className="ns-auth-form" role={done ? 'status' : 'alert'}>
        <span className="ns-auth-ic">
          <Icon name={done ? 'check-circle' : 'alert'} />
        </span>
        <h1>{t(done ? 'auth.reset.doneTitle' : 'auth.forgot.title')}</h1>
        <p className="ns-auth-sub">{t(done ? 'auth.reset.done' : 'ns.err.100043')}</p>
        <Btn kind="primary" onClick={() => go(done ? 'signin' : 'forgot')} className="ns-wide">
          {t(done ? 'ns.signin.submit' : 'auth.reset.newLink')}
        </Btn>
        {!done ? (
          <p className="ns-auth-alt">
            <button type="button" className="ns-link" onClick={back}>
              {t('c.back')}
            </button>
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <form className="ns-auth-form" onSubmit={submit} noValidate>
      <h1>{t('auth.reset.title')}</h1>
      <p className="ns-auth-sub">{t('auth.reset.body')}</p>
      <Field label={t('ns.pw.new')} help={t('auth.password.help')} error={msg}>
        <PasswordInput
          autoComplete="new-password"
          value={password}
          aria-invalid={!!msg || undefined}
          onBlur={() => setTouched(true)}
          onChange={(e) => (setPassword(e.target.value), setErr(null))}
          autoFocus={autoFocusOk()}
        />
      </Field>
      <Btn
        kind="primary"
        type="submit"
        disabled={!ok || busy}
        busy={busy}
        className="ns-wide ns-auth-submit"
      >
        {t('auth.reset.submit')}
      </Btn>
      <p className="ns-auth-alt">
        <button
          type="button"
          className="ns-link"
          onClick={() => (forgetLinkParam('reset_password'), back())}
        >
          {t('c.back')}
        </button>
      </p>
    </form>
  );
}

function Underage({ go }: { go(s: Step): void }) {
  const { t } = useT();
  return (
    <div className="ns-auth-form ns-underage" role="alert">
      <span className="ns-auth-ic">
        <Icon name="hand" />
      </span>
      <h1>{t('ns.underage.title')}</h1>
      <p>{t('auth.birth.tooYoung', { brand: t('ns.brand') })}</p>
      <Btn kind="ghost" onClick={() => go('signin')} className="ns-wide">
        {t('ns.signin.submit')}
      </Btn>
    </div>
  );
}

export function AuthScreen({ offline, onRetry }: { offline?: boolean; onRetry?: () => void }) {
  const { t } = useT();
  const blockedUntil = Number(storage.get(UNDERAGE_KEY) ?? 0);
  // A reset link (`?reset_password=`) opens its screen first.
  const [resetToken] = useState(() => takeLinkParam('reset_password'));
  // An invite link (/invite/CODE): its server and channel are named here, and
  // sign-up comes first ("Open in browser" leads to the short sign-up).
  const [invite] = useState(() => takeInviteLink());
  const [step, setStep] = useState<Step>(() =>
    resetToken
      ? 'reset'
      : blockedUntil > Date.now()
        ? 'underage'
        : savedRecovery()
          ? 'recover'
          : signupLinkOpened()
            ? 'signup'
            : 'signin',
  );
  const [mfa, setMfa] = useState<MfaRequiredResponse | null>(null);
  const appName = useAppName();
  const wordmark = useWordmark();
  // a touch screen: the keyboard never hides the submit (§12.7 C3)
  const screen = useRef<HTMLDivElement>(null);
  useSubmitInView(screen);
  useEffect(() => {
    setTitleName(appName);
  }, [appName]);
  useEffect(() => forgetSignupLink, []);
  const go = (s: Step) => {
    if (s === 'signup' && Number(storage.get(UNDERAGE_KEY) ?? 0) > Date.now()) s = 'underage';
    setStep(s);
  };
  return (
    <div className="ns-auth" ref={screen}>
      <div className="nx-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>
      <div className="ns-auth-top">
        <div className="nx-tb-brand" dir="ltr">
          <img className="nx-tb-mark" src={markUrl} alt="" width={18} height={18} />
          <span className="nx-wm">{wordmark}</span>
          <span className="nx-wm-tag">ALPHA</span>
        </div>
      </div>
      {invite && !offline && (step === 'signin' || step === 'signup') ? (
        <InviteContext code={invite} />
      ) : null}
      <main className="ns-auth-card nx-glass strong">
        {offline ? (
          <div className="ns-auth-form" role="status">
            <span className="ns-auth-ic">
              <Icon name="alert" />
            </span>
            <h1>{t('ns.offline.title')}</h1>
            <p className="ns-auth-sub">{t('ns.offline.body')}</p>
            <Btn kind="primary" onClick={onRetry} className="ns-wide">
              {t('ns.msg.retry')}
            </Btn>
          </div>
        ) : step === 'signin' ? (
          <SignIn
            go={go}
            onMfa={(m) => {
              setMfa(m);
              setStep('mfa');
            }}
          />
        ) : step === 'signup' ? (
          <SignUp go={go} />
        ) : step === 'mfa' && mfa ? (
          <Mfa ticket={mfa} go={go} />
        ) : step === 'recover' ? (
          <Recovery back={() => go('signin')} forgot={() => go('forgot')} />
        ) : step === 'forgot' ? (
          <Forgot go={go} />
        ) : step === 'reset' && resetToken ? (
          <ResetPassword token={resetToken} go={go} />
        ) : (
          <Underage go={go} />
        )}
      </main>
      <DesktopServerLink />
      <p className="ns-auth-foot">{t('ns.auth.foot')}</p>
    </div>
  );
}
